import { Injectable } from '@angular/core';
import { CapacitorSQLite, CapacitorSQLitePlugin, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { BehaviorSubject, Observable } from 'rxjs';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { KeyValue } from '../entities/key-value';
import { environment } from '../../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class DatabaseService {
  private sqlite: SQLiteConnection;
  sqliteConnection!: SQLiteConnection;
  sqlitePlugin!: CapacitorSQLitePlugin;
  private database: SQLiteDBConnection | null = null;
  private isDatabaseReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private dbName: string = environment.sqliteDbName;
  keyValueList = new BehaviorSubject([]);
  constructor(
    private platform: Platform,
  ) {
  }

  async initializeDatabase(): Promise<boolean> {
    try {
      this.platform.ready().then(async () => {
        if (Capacitor.isNativePlatform()) {
          this.sqlite = new SQLiteConnection(CapacitorSQLite);
        }
      });
      return true;
    } catch (error) {
      console.error('Error initializing the app SQLite database:', error);
    }
  }
  async openDatabase(): Promise<boolean> {
    try {
      this.platform.ready().then(async () => {
        if (Capacitor.isNativePlatform()) {

          // Check connection consistency
          const retCC = (await this.sqlite.checkConnectionsConsistency()).result;

          // Check if connected
          const isConnection = (await this.sqlite.isConnection(this.dbName, false)).result;

          if (!isConnection && !retCC) {
            // Create a new connection
            this.database = await this.sqlite.createConnection(this.dbName, false, 'no-encryption', 1, false);
            await this.database.open();
            await this.createTable();
            this.isDatabaseReady.next(true);
          } else {
            // Retrieve an existing connection
            this.database = await this.sqlite.retrieveConnection(this.dbName, false);
            await this.database.open();
            this.isDatabaseReady.next(true);
          }
        }
      });
      return true;
    } catch (error) {
      console.error('Error setting up the app SQLite database:', error);
      return false;
    }
  }

  isReady() {
    return this.isDatabaseReady.asObservable();
  }

  private async createTable(): Promise<void> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    const createTableQuery = 'CREATE TABLE IF NOT EXISTS KeyValue (key VARCHAR(32) unique, value TEXT)';
    await this.database.execute(createTableQuery);
    console.log('Created KeyValue table')
  }


  async getKeyValue(key: string): Promise<KeyValue> {
    console.log("Searching db for key: " + key);
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    const query = 'SELECT * FROM KeyValue WHERE key = ?';
    const item = await this.database.query(query, [key]);
    console.log(item);
    if (!item || item.values.length === 0) {
      return null;
    }
    return {
      key: item.values.keys[0],
      value: item.values.values[0],
    }
  }

  async getKeyValues(): Promise<KeyValue[]> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    const query = 'SELECT * FROM KeyValue;'
    const items = await this.database.query(query);
    console.log('KeyValue table has ' + items.values.length + ' items');
    this.keyValueList.next(items.values || []);
    return items.values || [];
  }

  fetchKeyValues(): Observable<KeyValue[]> {
    return this.keyValueList.asObservable();
  }

  async addKeyValue(key: string, value: string): Promise<void> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    let data = [key, value];
    const query = 'INSERT INTO KeyValue (key, value) VALUES (?, ?)';
    await this.database.run(query, data);
    console.log('Added item -> ' + key + ":" + JSON.stringify(value));
    await this.getKeyValues();
  }

  async updateValue(key: string, value: string): Promise<void> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    let data = [value, key];
    const query = 'UPDATE KeyValue SET value = ? WHERE key = ?';
    await this.database.run(query, data);
    console.log('Updated item -> ' + key + ":" + value);
    await this.getKeyValues();
  }

  async deleteKeyValue(key: string): Promise<void> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    const query = 'DELETE FROM KeyValue WHERE key = ?';
    await this.database.run(query, [key]);
    console.log('Deleted item -> ' + key);
    this.getKeyValues();
  }

  async deleteAll(): Promise<void> {
    if (!this.database) {
      throw new Error('Database connection is not open');
    }
    const query = 'DELETE FROM KeyValue';
    await this.database.run(query);
    console.log('!!! Deleted all rows !!!');
    this.getKeyValues();
  }

  async deleteKeyValueTable(): Promise<void> {
    this.keyValueList.next([]);
    this.isDatabaseReady.next(false);
    await this.database.execute('DROP TABLE IF EXISTS KeyValue');
    console.log('!!! Dropped KeyValue table !!!');
    await this.database.close();
    await this.sqlite.closeConnection(environment.sqliteDbName, false);
  }

  async closeDatabase(): Promise<void> {
    if (this.database) {
      await this.sqlite.closeConnection(this.dbName, false);
      this.database = null;
      this.isDatabaseReady.next(false);
    }
  }
}
